import { z } from 'zod';

import { hashStringToInteger } from '../../utils/hash';
import defaultPaletteJson from './LKTranscriptSpeakerColors.default.json';

const normalizeHexColor = (value: unknown) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  return /^[0-9a-f]{6}$/i.test(trimmed) ? `#${trimmed}` : trimmed;
};

const LKTranscriptColorSchema = z.preprocess(
  normalizeHexColor,
  z.string().regex(/^#[0-9a-f]{6}$/i, 'Expected a six-digit CSS hex color')
);

const LKTranscriptColorItemSchema = z.object({
  speaker_color: LKTranscriptColorSchema,
  text_color: LKTranscriptColorSchema,
  highlight_color: LKTranscriptColorSchema,
  background_color: LKTranscriptColorSchema,
});

const LKTranscriptThemeSchema = z.object({
  light_theme: LKTranscriptColorItemSchema,
  dark_theme: LKTranscriptColorItemSchema,
});

export const LKTranscriptSpeakerPaletteSchema = z.object({
  solo: LKTranscriptThemeSchema,
  me: LKTranscriptThemeSchema,
  others: z.array(LKTranscriptThemeSchema).min(1),
});

export type LKTranscriptColorItem = z.infer<typeof LKTranscriptColorItemSchema>;
export type LKTranscriptSpeakerPalette = z.infer<typeof LKTranscriptSpeakerPaletteSchema>;
export type LKTranscriptThemeMode = 'light_theme' | 'dark_theme';

export const DEFAULT_LK_TRANSCRIPT_SPEAKER_PALETTE = LKTranscriptSpeakerPaletteSchema.parse(defaultPaletteJson);

export const parseLKTranscriptSpeakerPalette = (value: unknown): LKTranscriptSpeakerPalette | undefined => {
  try {
    const parsedJson = typeof value === 'string' ? JSON.parse(value) : value;
    const result = LKTranscriptSpeakerPaletteSchema.safeParse(parsedJson);
    return result.success ? result.data : undefined;
  } catch {
    return undefined;
  }
};

type LKSpeakerRecord = {
  avaId: string;
  order?: number;
};

export type LKTranscriptSpeakerColorAssignment = {
  colors: LKTranscriptColorItem;
  isMe: boolean;
};

type OrderedSpeaker = LKSpeakerRecord & {
  speakerKey: string;
};

export const buildLKTranscriptSpeakerColorAssignments = ({
  palette,
  mode,
  speakersById,
  speakerIdEnabledByAvaId,
  participantAvaIds,
  displaySpeakerIds,
}: {
  palette: LKTranscriptSpeakerPalette;
  mode: LKTranscriptThemeMode;
  speakersById: Record<string, LKSpeakerRecord>;
  speakerIdEnabledByAvaId: Record<string, boolean | undefined>;
  participantAvaIds: string[];
  displaySpeakerIds?: string[];
}) => {
  const orderedSpeakers = Object.entries(speakersById)
    .flatMap<OrderedSpeaker>(([speakerKey, speaker]) => (speaker.order == null ? [] : [{ speakerKey, ...speaker }]))
    .sort((first, second) => (first.order ?? 0) - (second.order ?? 0));

  // Production passes the identities resolved by transcript presentation so
  // explicit speaker overrides are counted. The fallback path supports callers
  // that only have the roster and speaker-ID setting.
  const effectiveSpeakers = new Map<string, OrderedSpeaker>();
  if (displaySpeakerIds != null) {
    const firstDisplayIndex = new Map<string, number>();
    displaySpeakerIds.forEach((speakerKey, index) => {
      if (!firstDisplayIndex.has(speakerKey)) firstDisplayIndex.set(speakerKey, index);
    });
    for (const [speakerKey] of firstDisplayIndex) {
      const speaker = speakersById[speakerKey];
      const ownerOrder = orderedSpeakers.find((candidate) => candidate.avaId === speakerKey)?.order;
      effectiveSpeakers.set(speakerKey, {
        speakerKey,
        avaId: speaker?.avaId ?? speakerKey,
        order: speaker?.order ?? ownerOrder,
      });
    }
    const sorted = Array.from(effectiveSpeakers.values()).sort((first, second) => {
      const firstOrder = first.order ?? Number.POSITIVE_INFINITY;
      const secondOrder = second.order ?? Number.POSITIVE_INFINITY;
      if (firstOrder !== secondOrder) return firstOrder - secondOrder;
      return (firstDisplayIndex.get(first.speakerKey) ?? 0) - (firstDisplayIndex.get(second.speakerKey) ?? 0);
    });
    effectiveSpeakers.clear();
    sorted.forEach((speaker) => effectiveSpeakers.set(speaker.speakerKey, speaker));
  } else {
    for (const speaker of orderedSpeakers) {
      const effectiveSpeakerKey =
        speaker.speakerKey.startsWith('speaker_') && !speakerIdEnabledByAvaId[speaker.avaId]
          ? speaker.avaId
          : speaker.speakerKey;
      const current = effectiveSpeakers.get(effectiveSpeakerKey);
      if (current == null) {
        effectiveSpeakers.set(effectiveSpeakerKey, { ...speaker, speakerKey: effectiveSpeakerKey });
      }
    }
  }

  const spokenSpeakers = Array.from(effectiveSpeakers.values());
  const soleParticipantAvaId = participantAvaIds.length === 1 ? participantAvaIds[0] : undefined;
  const isSolo = soleParticipantAvaId != null && spokenSpeakers.length === 1;
  const isMeSpeaker = (speaker: OrderedSpeaker) =>
    soleParticipantAvaId != null && speaker.speakerKey === soleParticipantAvaId;
  const otherSpeakers = spokenSpeakers.filter((speaker) => !isMeSpeaker(speaker));
  const assignments = new Map<string, LKTranscriptSpeakerColorAssignment>();

  for (const speaker of spokenSpeakers) {
    const isMe = isMeSpeaker(speaker);
    const otherIndex = otherSpeakers.findIndex((candidate) => candidate.speakerKey === speaker.speakerKey);
    const theme = isSolo
      ? palette.solo
      : isMe
      ? palette.me
      : palette.others[Math.max(0, otherIndex) % palette.others.length];
    assignments.set(speaker.speakerKey, { colors: theme[mode], isMe });
  }

  // Speaker updates are broadcast before their transcript turn, but retain a
  // deterministic fallback for startup races and malformed historical data.
  for (const speakerKey of Object.keys(speakersById)) {
    if (assignments.has(speakerKey)) continue;
    const isMe = soleParticipantAvaId != null && speakerKey === soleParticipantAvaId;
    const fallbackTheme = isMe ? palette.me : palette.others[hashStringToInteger(speakerKey) % palette.others.length];
    assignments.set(speakerKey, { colors: fallbackTheme[mode], isMe });
  }

  return {
    assignments,
    spokenSpeakerCount: spokenSpeakers.length,
  };
};
